import React from "react";
import { TONE_CLASSES, statusMeta } from "../broadcastStatus";

/**
 * The Broadcasts status badge: a 5px dot and a word, on a tinted pill.
 *
 * ACCESSIBILITY: the word is ALWAYS rendered. Status is never colour alone —
 * that is a WCAG 1.4.1 requirement, not a stylistic preference. The dot is
 * decorative reinforcement (`bg-current`, so it can never disagree with the
 * text colour) and is hidden from assistive tech.
 *
 * The palette is imported from `broadcastStatus`, not restated here: a badge
 * and the status table it renders must not be able to drift apart.
 *
 * @param {Object} props
 * @param {'ok'|'warn'|'bad'|'neut'|'accent'} props.tone - A key of TONE_CLASSES.
 * @param {React.ReactNode} props.children - The status word.
 * @param {string} [props.className] - Extra classes.
 */
export const Badge = ({ tone, children, className = "" }) => {
  const toneClass = TONE_CLASSES[tone] || TONE_CLASSES.neut;

  return (
    <span
      className={`inline-flex items-center gap-[5px] rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${toneClass} ${className}`}
    >
      <i className="h-[5px] w-[5px] rounded-full bg-current" aria-hidden="true" />
      {children}
    </span>
  );
};

/**
 * A decoded broadcast row rendered as its status badge.
 *
 * Named `BroadcastStatusBadge`, not `StatusBadge`, because
 * `src/common/comms/StatusBadge.jsx` already owns that name for the older
 * Support/Messages palette. Two components called `StatusBadge` with different
 * colour maps is a merge conflict waiting to look like a design decision.
 *
 * @param {Object} props
 * @param {Object|string|null|undefined} props.row - A decoded row, or a bare status string.
 * @param {string} [props.className] - Extra classes.
 */
export const BroadcastStatusBadge = ({ row, className = "" }) => {
  const meta = statusMeta(row);
  return (
    <Badge tone={meta.tone} className={className}>
      {meta.label}
    </Badge>
  );
};
